"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { setNavLoading } from "@/components/navLoadingStore";

/**
 * Top-of-viewport navigation progress bar (GitHub/YouTube style) for the whole
 * app. App Router gives no built-in "route is loading" signal for soft
 * navigations, so we detect the START from same-origin link clicks + form
 * submits, and the FINISH when the URL actually commits (pathname/searchParams
 * change). It also drives navLoadingStore so the board can dim its results.
 *
 * The bar is an indeterminate sliding segment (honest — we don't know real
 * progress). A safety timer clears a stuck bar if a click never commits (e.g. a
 * no-op link); no-op navigations are ignored up front.
 */
export function NavProgress() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const urlKey = `${pathname}?${searchParams.toString()}`;
  const prev = useRef(urlKey);
  const safety = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const clearSafety = () => {
      if (safety.current) {
        clearTimeout(safety.current);
        safety.current = null;
      }
    };
    const start = () => {
      setActive(true);
      setNavLoading(true);
      clearSafety();
      safety.current = setTimeout(() => {
        setActive(false);
        setNavLoading(false);
      }, 12000);
    };

    const onClick = (e: MouseEvent) => {
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      )
        return;
      const anchor = (e.target as HTMLElement | null)?.closest?.("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (
        !href ||
        href.startsWith("#") ||
        anchor.target === "_blank" ||
        anchor.hasAttribute("download") ||
        anchor.getAttribute("rel")?.includes("external")
      )
        return;
      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      // Same URL = no navigation; don't start a bar that will never finish.
      if (
        url.pathname + url.search ===
        window.location.pathname + window.location.search
      )
        return;
      start();
    };

    // The board search is a client-nav form submit — cover it too.
    const onSubmit = () => start();

    document.addEventListener("click", onClick, true);
    document.addEventListener("submit", onSubmit, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("submit", onSubmit, true);
      clearSafety();
    };
  }, []);

  // Commit: the URL changed → finish.
  useEffect(() => {
    if (urlKey === prev.current) return;
    prev.current = urlKey;
    if (safety.current) {
      clearTimeout(safety.current);
      safety.current = null;
    }
    setNavLoading(false);
    setActive(false);
  }, [urlKey]);

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-[3px] overflow-hidden"
    >
      <div
        className={`h-full w-[30%] bg-brand-600 shadow-[0_0_10px_rgba(37,71,240,0.6)] transition-opacity duration-300 ${
          active ? "nav-slide opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
}
